import { customRef, onUnmounted } from "vue";

function toBool(str) {
  return str == "true";
}

function getValue<T>(
  value: string | null,
  type:
    | BooleanConstructor
    | StringConstructor
    | NumberConstructor
    | ArrayConstructor
    | ObjectConstructor
) {
  if (value === null) return null;
  switch (type) {
    case Boolean:
      return toBool(value);
    case String:
      return value.toString();
    case Number:
      return parseFloat(value);
    case Array:
      return JSON.parse(value) as T;
    case Object:
      return JSON.parse(value) as T;
    default:
      return null;
  }
}

type StorageValCallback = (newVal: string) => void;
const ListenerMap = new Map<string, StorageValCallback[]>();

function getLocalStorage(key: string) {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function setLocalStorage(key: string, value: string) {
  try {
    return localStorage.setItem(key, value);
  } catch (e) {}
}

window.addEventListener("storage", (e: StorageEvent) => {
  if (e.key) {
    if (ListenerMap.has(e.key)) {
      const newVal = getLocalStorage(e.key);
      if (newVal)
        ListenerMap.get(e.key)?.forEach(triggerUpdate => triggerUpdate(newVal));
    }
  }
});

function addListener(key: string, cb: StorageValCallback) {
  if (!ListenerMap.has(key)) ListenerMap.set(key, []);
  ListenerMap.get(key)?.push(cb);
}

function deleteListener(key: string, cb: StorageValCallback) {
  if (ListenerMap.has(key)) {
    const fnArray = ListenerMap.get(key);
    if (fnArray) {
      const matchedIndex = fnArray.indexOf(cb);
      fnArray.splice(matchedIndex, 1);
    }
  }
}

type DefaultUnwrapper<T> = T extends (infer U)[]
  ? U[]
  : T extends boolean
  ? boolean
  : T extends number
  ? number
  : T extends string
  ? string
  : T extends Record<string, any>
  ? T
  : never;

export function createLocalStorageRef<T, Unrwapped = DefaultUnwrapper<T>>(
  name: string,
  type:
    | BooleanConstructor
    | StringConstructor
    | NumberConstructor
    | ObjectConstructor
    | ArrayConstructor,
  defaultValue: T
) {
  const itemName = "fresh8-ads-" + name;

  const firstValue = getValue(getLocalStorage(itemName) as string, type);

  let value =
    firstValue !== null
      ? (firstValue as Unrwapped)
      : ((defaultValue as unknown) as Unrwapped);

  let cb: StorageValCallback;

  const ref = customRef((track, trigger) => {
    cb = newValue => {
      const typedNewValue = getValue(newValue, type) as Unrwapped;
      if (value !== typedNewValue) {
        value = typedNewValue;
        trigger();
      }
    };

    addListener(itemName, cb);

    return {
      get() {
        track();
        return value;
      },
      set(newValue: Unrwapped) {
        setLocalStorage(itemName, JSON.stringify(newValue));
        value = newValue;
        trigger();
      }
    };
  });

  return {
    item: ref,
    cleanup: () => deleteListener(itemName, cb)
  };
}

export function useLocalstorageRef<T>(
  name: string,
  type:
    | BooleanConstructor
    | StringConstructor
    | NumberConstructor
    | ObjectConstructor
    | ArrayConstructor,
  defaultValue: T
) {
  const { item, cleanup } = createLocalStorageRef(name, type, defaultValue);

  onUnmounted(() => cleanup);

  return item;
}
